import { useId, useMemo } from "react";
import { Link } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PolylineOutlinedIcon from "@mui/icons-material/PolylineOutlined";
import {
  BOUNDARY_PREVIEW_HEIGHT,
  BOUNDARY_PREVIEW_WIDTH,
  buildBoundaryPreview,
} from "../utils/fieldBoundaryPreview.js";

export default function FieldBoundaryPreview({ field, editTo }) {
  const titleId = useId();
  const descriptionId = useId();
  const preview = useMemo(() => buildBoundaryPreview(field?.boundaries), [field?.boundaries]);
  const hasVertices = Array.isArray(field?.boundaries) && field.boundaries.length > 0;
  const hasCoordinates = Number.isFinite(field?.latitude) && Number.isFinite(field?.longitude);
  const summary = preview
    ? `${preview.pieceCount} ${preview.pieceCount === 1 ? "piece" : "pieces"}${preview.exclusionCount ? ` · ${preview.exclusionCount} ${preview.exclusionCount === 1 ? "exclusion" : "exclusions"}` : ""}`
    : "";

  return (
    <section aria-label="Saved field boundary" className="flex items-center gap-4 border-b border-slate-200 pb-4">
      {preview ? (
        <svg
          aria-labelledby={`${titleId} ${descriptionId}`}
          className="h-[104px] w-[112px] shrink-0 rounded-[8px] border border-emerald-100 bg-[#f0f6ef] sm:h-[112px] sm:w-[184px]"
          role="img"
          viewBox={`0 0 ${BOUNDARY_PREVIEW_WIDTH} ${BOUNDARY_PREVIEW_HEIGHT}`}
        >
          <title id={titleId}>{field?.name || "Field"} saved boundary</title>
          <desc id={descriptionId}>{summary}. North is up. Exclusions are unfilled.</desc>
          {preview.paths.map(({ polygon, d }) => (
            <path key={polygon} d={d} fill="#34a853" fillOpacity="0.18" fillRule="evenodd" stroke="#24743b" strokeLinejoin="round" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          ))}
        </svg>
      ) : (
        <PolylineOutlinedIcon aria-hidden="true" className="shrink-0 text-slate-400" />
      )}
      <div className="min-w-0">
        <h2 className="text-sm font-bold text-slate-800">{preview ? "Saved boundary" : hasVertices ? "Boundary preview unavailable" : "No boundary saved"}</h2>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          {preview ? `${summary} · North up` : hasVertices ? "Review the saved boundary in Edit Field." : hasCoordinates ? "Coordinates saved. Add a boundary to see its outline." : "Add a boundary to see its outline."}
        </p>
        {editTo ? (
          <Link className="mt-1 inline-flex min-h-[40px] items-center gap-1 rounded px-1 text-sm font-semibold text-[#2f6f45] hover:underline focus:outline-none focus:ring-2 focus:ring-[#34a853]/35" to={editTo}>
            <EditOutlinedIcon fontSize="inherit" />
            {preview || hasVertices ? "Edit boundary" : "Add boundary"}
          </Link>
        ) : null}
      </div>
    </section>
  );
}
